import { useState, useEffect } from 'react'
import { Assessment } from '../types/assessment'

const LIBRARY_KEY_PREFIX = 'provenance_one_library_'

export function useAssessmentLibrary(userEmail: string | null) {
  const [assessments, setAssessments] = useState<Assessment[]>([])

  const getStorageKey = () => {
    return userEmail ? `${LIBRARY_KEY_PREFIX}${userEmail}` : null
  }

  // Load assessments from localStorage
  useEffect(() => {
    const key = getStorageKey()
    if (!key) {
      setAssessments([])
      return
    }

    const stored = localStorage.getItem(key)
    if (stored) {
      try {
        setAssessments(JSON.parse(stored))
      } catch (e) {
        console.error('Failed to load assessments:', e)
        setAssessments([])
      }
    } else {
      setAssessments([])
    }
  }, [userEmail])

  // Save assessments to localStorage
  const saveAssessments = (newAssessments: Assessment[]) => {
    const key = getStorageKey()
    if (!key) return

    localStorage.setItem(key, JSON.stringify(newAssessments))
    setAssessments(newAssessments)
  }

  const addAssessment = (assessment: Assessment) => {
    const updated = [...assessments, assessment]
    saveAssessments(updated)
  }

  const updateAssessment = (
    assessmentId: string,
    updates: Partial<Assessment>,
  ) => {
    const updated = assessments.map((a) =>
      a.id === assessmentId
        ? { ...a, ...updates, updatedAt: new Date().toISOString() }
        : a,
    )
    saveAssessments(updated)
  }

  const deleteAssessment = (assessmentId: string) => {
    const updated = assessments.filter((a) => a.id !== assessmentId)
    saveAssessments(updated)
  }

  const getAssessment = (assessmentId: string): Assessment | undefined => {
    return assessments.find((a) => a.id === assessmentId)
  }

  return {
    assessments,
    addAssessment,
    updateAssessment,
    deleteAssessment,
    getAssessment,
  }
}
